import React from 'react';
import { getContext, getEasing, getDuration, isAnimationDisabled } from 'common';
import InfoIcon from '@nokia-csf-uxr/ccfk-assets/InfoIcon';
import WarningIcon from '@nokia-csf-uxr/ccfk-assets/WarningIcon';
import ErrorIcon from '@nokia-csf-uxr/ccfk-assets/ErrorIcon';
import CheckCircleIcon from '@nokia-csf-uxr/ccfk-assets/CheckCircleIcon';
import CloseIcon from '@nokia-csf-uxr/ccfk-assets/CloseIcon';
import IconButton from 'components/IconButton';
import { useTransition, animated } from 'react-spring';
import { TRANSITION_ACCELERATE } from '@nokia-csf-uxr/freeform-design-tokens/tokens/transition';
import { SPEED_SLOW } from '@nokia-csf-uxr/freeform-design-tokens/tokens/speed';
import _merge from 'lodash/merge';
import Styled from './styles';

const DEFAULT_ANIMATION = disableAnimation => ({
  initial: { opacity: 1 },
  leave: { opacity: 0 },
  config: {
    duration: getDuration(SPEED_SLOW), // 300ms
    easing: getEasing(TRANSITION_ACCELERATE) // cubic-bezier (0.4, 0, 1, 1)
  },
  immediate: disableAnimation,
});

const resolveIcon = (variant, theme) => {
  switch (variant) {
    case 'warning':
      return <WarningIcon color={theme.warning.icon} />;
    case 'error':
      return <ErrorIcon color={theme.error.icon} />;
    case 'success':
      return <CheckCircleIcon color={theme.success.icon} />;
    default:
      return <InfoIcon color={theme.info.icon} />;
  }
};

export interface InlineFeedbackMessageProps extends React.ComponentPropsWithRef<'div'> {
  /** Content of the InlineFeedbackMessageProps. */
  children: React.ReactNode;
  /** Toggle between the variants of Inline Feedback Messages. */
  variant?: 'info' | 'warning' | 'error' | 'success';
  /** Controls the display of the Inline Feedback Message. Use this to control the component. */
  show?: boolean;
  /** Render a custom icon (to replace the default search icon). See <IconButton /> and <Icon/> component for more information. */
  renderIcon?: React.ReactNode;
  /** Render a custom close button (to replace the default). */
  renderCloseButton?: React.ReactNode;
  /** A render slot to render a <Hyperlink /> component. See <Hyperlink /> component for more information. This component will be right-aligned within the Inline Feedback Message. */
  renderHyperlink?: React.ReactNode;
  /** A render slot to render a <CircularProgressIndicatorIndeterminate /> component. See <CircularProgressIndicatorIndeterminate /> component for more information. This component will be right-aligned within the Inline Feedback Message. */
  renderProgressIndicator?: React.ReactNode;
  /** Show close button. */
  closeButton?: boolean;
  /** 'onClose' callback */
  onClose?: (arg: React.MouseEvent | React.KeyboardEvent) => void;
  /** Controls the animation. Uses react-spring transitions config, see 'react-spring' package for more info (https://www.react-spring.io/) */
  animation?: {};
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** Override the theme. Please refer to the "Readme" tab to learn more on theme structure. */
  theme?: any;
}

/**
 * An inline feedback message is a temporary, embedded notification that acts to inform the user about a significant condition that they should be made aware of.
 * All additional props will be forwarded to the root element.
 */

const InlineFeedbackMessage = React.forwardRef<HTMLElement, InlineFeedbackMessageProps>((props: InlineFeedbackMessageProps, ref) =>
  getContext(({ RTL, theme: globalTheme, disableAnimation }) => {
    const {
      renderIcon,
      variant,
      show,
      renderHyperlink,
      renderProgressIndicator,
      renderCloseButton,
      closeButton,
      children,
      onClose,
      animation,
      disableAnimation: propsdisableAnimation,
      theme,
      ...otherProps
    } = props;
    const transitions = useTransition(show, null, animation || DEFAULT_ANIMATION(isAnimationDisabled(propsdisableAnimation, disableAnimation)));
    const resolvedTheme = _merge({}, globalTheme.INLINE_FEEDBACK_MESSAGES, theme);

    return transitions.map(({ item, key, props: transitionsStyles }) =>
      item && (
      <Styled.InlineFeedbackMessage
        as={animated.div}
        key={key}
        style={transitionsStyles}
        $rtl={RTL}
        ref={ref}
        $variant={variant}
        $theme={resolvedTheme}
        role="alert"
        {...otherProps}
      >
        {renderIcon || (
        <Styled.InlineFeedbackMessageIcon $rtl={RTL}>{resolveIcon(variant, resolvedTheme)}</Styled.InlineFeedbackMessageIcon>
        )}
        <Styled.InlineFeedbackMessageContent
          $rtl={RTL}
          $closeButton={closeButton || renderCloseButton}
          $hyperlink={renderHyperlink}
          $theme={resolvedTheme}
          $variant={variant}
        >
          {children}
        </Styled.InlineFeedbackMessageContent>
        {renderHyperlink && (
        <Styled.InlineFeedbackMessageHyperlink $theme={resolvedTheme} $rtl={RTL}>
          {renderHyperlink}
        </Styled.InlineFeedbackMessageHyperlink>
        )}
        {renderProgressIndicator && (
        <Styled.InlineFeedbackMessageProgressIndicator $rtl={RTL}>
          {renderProgressIndicator}
        </Styled.InlineFeedbackMessageProgressIndicator>
        )}
        {closeButton &&
              (renderCloseButton || (
                <Styled.InlineFeedbackMessageCloseButton $rtl={RTL}>
                  <IconButton onClick={onClose} aria-label="dismiss">
                    <CloseIcon />
                  </IconButton>
                </Styled.InlineFeedbackMessageCloseButton>
              ))}
      </Styled.InlineFeedbackMessage>
      ));
  }));

InlineFeedbackMessage.defaultProps = {
  renderIcon: undefined,
  renderCloseButton: undefined,
  renderHyperlink: undefined,
  renderProgressIndicator: undefined,
  closeButton: false,
  onClose: undefined,
  variant: 'info',
  show: true,
  animation: undefined,
  disableAnimation: undefined,
  theme: {}
};
InlineFeedbackMessage.displayName = 'InlineFeedbackMessage';

/* Exports */
export { InlineFeedbackMessage as default };
